import { AuthService } from 'services/AuthService';
import { HttpClient } from '../services/http-client';
import { AdminListingService } from 'services/AdminListingService';
import { AdminRegionService } from 'services/AdminRegionService';
import { AdminLocaleService } from 'services/AdminLocaleService';
import { AdminAmenityService } from 'services/AdminAmenityService';
import { AdminTagService } from 'services/AdminTagService';
import { AdminTermService } from 'services/AdminTermService';
import { AdminOptionService } from 'services/AdminOptionService';
import { AdminLandmarkService } from 'services/AdminLandmarkService';
import { AdminPromotionService } from 'services/AdminPromotionService';
import { AdminBusinessService } from 'services/AdminBusinessService';
import { AdminBedTypeService } from 'services/AdminBedTypeService';
import { AdminMemberService } from 'services/AdminMemberService';
import { AdminReservationService } from 'services/AdminReservationService';
import { UserService } from 'services/UserService';
import { AdminUserService } from 'services/AdminUserService';
import { AdminVoucherService } from 'services/AdminVoucherService';
import { AdminCouponService } from 'services/AdminCouponService';
import { PaymentService } from 'services/PaymentService';
import { AdminMessageService } from 'services/AdminMessageService';
import { MyAccountService } from 'services/MyAccountService';
import { AdminRefundPolicyService } from 'services/AdminRefundPolicyService';
import { AdminMembershipPolicyService } from 'services/AdminMembershipPolicyService';
import { AdminPointPolicyService } from 'services/AdminPointPolicyService';

type ServiceInstances = {
  locationService?: Location;
  authService?: AuthService;
  adminListingService?: AdminListingService;
  adminRegionService?: AdminRegionService;
  adminLocaleService?: AdminLocaleService;
  adminAmenityService?: AdminAmenityService;
  adminTagService?: AdminTagService;
  adminTermsService?: AdminTermService;
  adminOptionService?: AdminOptionService;
  adminLandmarkService?: AdminLandmarkService;
  adminPromotionService?: AdminPromotionService;
  adminBusinessService?: AdminBusinessService;
  adminBedTypeService?: AdminBedTypeService;
  adminMemberService?: AdminMemberService;
  userService?: UserService;
  adminReservationService?: AdminReservationService;
  adminVoucherService?: AdminVoucherService;
  // 다른 서비스 클래스를 여기에 추가하세요.
  adminUserService?: AdminUserService;
  adminCouponService?: AdminCouponService;
  paymentService?: PaymentService;
  adminMessageService?: AdminMessageService;
  myAccountService?: MyAccountService;
  adminRefundPolicyService?: AdminRefundPolicyService;
  adminMembershipPolicyService?: AdminMembershipPolicyService;
  adminPointPolicyService?: AdminPointPolicyService;
};

// 서비스 헬퍼 클래스를 정의
class ServiceHelper {
  private services: ServiceInstances = {};
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    // HttpClient에 업로드 진행률 설정 추가
    this.httpClient = new HttpClient({
      onUploadProgress: (progressEvent: any) => {
        if (
          progressEvent.body &&
          typeof progressEvent.body === 'object' &&
          'onProgress' in progressEvent.body
        ) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressEvent.body.onProgress(progress);
        }
      },
    });
    this.loadServices();
  }

  // 서비스 클래스 목록을 명시적으로 정의
  private loadServices() {
    // 서비스 클래스들을 명시적으로 정의하여 인스턴스화
    this.services = {
      authService: new AuthService(this.httpClient),
      adminListingService: new AdminListingService(this.httpClient),
      adminLocaleService: new AdminLocaleService(this.httpClient),
      adminRegionService: new AdminRegionService(this.httpClient),
      adminAmenityService: new AdminAmenityService(this.httpClient),
      adminTagService: new AdminTagService(this.httpClient),
      adminTermsService: new AdminTermService(this.httpClient),
      adminOptionService: new AdminOptionService(this.httpClient),
      adminLandmarkService: new AdminLandmarkService(this.httpClient),
      adminPromotionService: new AdminPromotionService(this.httpClient),
      adminBusinessService: new AdminBusinessService(this.httpClient),
      adminBedTypeService: new AdminBedTypeService(this.httpClient),
      adminMemberService: new AdminMemberService(this.httpClient),
      userService: new UserService(this.httpClient),
      adminReservationService: new AdminReservationService(this.httpClient),
      adminVoucherService: new AdminVoucherService(this.httpClient),
      adminCouponService: new AdminCouponService(this.httpClient),
      // 다른 서비스들도 필요하면 여기에 추가하세요.
      adminUserService: new AdminUserService(this.httpClient),
      paymentService: new PaymentService(this.httpClient),
      adminMessageService: new AdminMessageService(this.httpClient),
      myAccountService: new MyAccountService(this.httpClient),
      adminRefundPolicyService: new AdminRefundPolicyService(this.httpClient),
      adminMembershipPolicyService: new AdminMembershipPolicyService(this.httpClient),
      adminPointPolicyService: new AdminPointPolicyService(this.httpClient),
    };
  }

  // 타입을 명시한 Proxy 반환
  getServiceProxy(): ServiceInstances {
    return new Proxy(this.services, {
      get: (target: ServiceInstances, prop: keyof ServiceInstances) => {
        if (prop in target) {
          const service = target[prop];
          if (service !== undefined) {
            return service;
          } else {
            throw new Error(`Service '${String(prop)}' has not been initialized.`);
          }
        } else {
          throw new Error(`Service '${String(prop)}' does not exist.`);
        }
      },
    }) as ServiceInstances;
  }
}

// HttpClient 인스턴스를 생성하고 헬퍼 클래스를 초기화
const httpClient = new HttpClient();
const serviceHelper = new ServiceHelper(httpClient);

// 싱글턴으로 내보내기
export default serviceHelper.getServiceProxy();
